<script setup>
  import { computed, watchEffect } from 'vue'
  import { useSpotStore } from '@/stores/SpotStore';
  import { useUserStore } from "@/stores/UserStore";
  import { useCompleteReportStore } from '@/stores/CompleteReportStore';
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import ModalCompleteReportSettings from '@/components/ModalCompleteReportSettings.vue';
  import WebViewVersionNumber from '@/components/WebViewVersionNumber.vue';
  import Icon from "@/components/icons/Icon.vue";
  import useSettingsGlobals from '@/composables/useSettingsGlobals';
  import useAdConfigurationGlobals from '@/composables/useAdConfigurationGlobals';

  const { $WEB_VIEW } = useSettingsGlobals();   
  const { $ADS_ALLOWED } = useAdConfigurationGlobals();   
 
  const spotStore = useSpotStore();
  const userStore = useUserStore();
  const completeReportStore = useCompleteReportStore();

  const isSmallDevice = useMediaQuery('(max-width: 550px)');

  const modelUpgradeBlocks = [1000, 1001];

  const modelUpgradeBlock = computed(() => modelUpgradeBlocks.map(block => {
      return completeReportStore.components[block].show() ? block : false;
    }).find(value => value)
  );
  const showModelUpgradeBlock = computed(() => modelUpgradeBlock.value );
  const modelUpgradeBlockIndex = computed(() => modelUpgradeBlocks.map(block => completeReportStore.containers.indexOf(block)).find(index => index > -1));

  function setPrimaryContainers() {
    completeReportStore.clearContainers();

    for (let i = 0; i < completeReportStore.containerOrder.length; i++) {
      const component = completeReportStore.components[completeReportStore.containerOrder[i]];

      if (component && component.show()) {
        completeReportStore.addContainer(completeReportStore.containerOrder[i]);
      }
    }

    placeMarketingContainers()
  };

  function placeMarketingContainers() {  
    if (showModelUpgradeBlock.value) {
      let spliceIndex = 1
      
      // If the wind graph contiainer is present and not displaying an upgrade message, place the model upgrade block before it
      // Else if the forecast table container is present, place it before that
      // Otherwise, place it after the current conditions container
      if (completeReportStore.containers.indexOf(1) > -1 && !spotStore.upgradeAvailable) {
        spliceIndex = completeReportStore.containers.indexOf(1);
      } 
      else if (completeReportStore.containers.indexOf(2) > -1) {
        spliceIndex = completeReportStore.containers.indexOf(2);
      }

      if (spliceIndex < 0) spliceIndex = 0;

      completeReportStore.insertContainer(modelUpgradeBlock.value, spliceIndex);
    }
    
    if ($ADS_ALLOWED) placeAds();
  }

  function placeAds() {
    const count = [...completeReportStore.containers].length;
    const startingIndex = showModelUpgradeBlock.value ? modelUpgradeBlockIndex.value : 1;
    const adCount = Math.floor((count - startingIndex) / 2);
    let currentIndex = startingIndex + 2;

    for (let i = 0; i < adCount; i++) {
      if (currentIndex <= completeReportStore.containers.length - 1) {
        completeReportStore.insertContainer(1100, currentIndex);
        currentIndex += 3;
      }
    }

  }

  
  if (!completeReportStore.error) {
    await Promise.allSettled(completeReportStore.requests);
    await userStore.getAPIStorage();

    // completeReportStore.containerCheck();

    setPrimaryContainers();
    completeReportStore.updateLoadingStatus(false);
  }


  watchEffect(() => {
    completeReportStore.activeContainers && setPrimaryContainers() && placeMarketingContainers();
  })
</script>

<template>
  <div v-if="!completeReportStore.isLoading && !completeReportStore.error" class="jwx-complete-report__main">
    <div v-if="(spotStore.spotMessage || spotStore.statusMessage) && !isSmallDevice && $WEB_VIEW" class="jwx-current-conditions-messages" style="--background-color: var(--jwx-surface-level-1)">
      <p v-if="spotStore.spotMessage" class="jwx-current-conditions-message jwx-page-header__meta"><span class="jwx-page__meta-label">Special Note:</span><br><span v-html= spotStore.spotMessage></span></p>
      <p v-if="spotStore.statusMessage" class="jwx-current-conditions-message jwx-page-header__meta"><span class="jwx-page__meta-label">Status Message:</span> {{ spotStore.statusMessage }}</p>
    </div>
    <component 
      v-for="(containerId, index) in completeReportStore.containers"
      :is="{...completeReportStore.components[containerId].component}"
      v-bind="{...completeReportStore.components[containerId].props}"
      :key="`${containerId}-${index}`"
    />
    
    <button
      v-if="$WEB_VIEW && userStore.nonTrialMemberLevel > -1"
      @click="completeReportStore.showSettingsModal = true"
      type="button"
      class="jwx-button jwx-button--pill"
      style="margin-block: var(--jwx-block-spacing)"
      id="complete-report-settings"
      data-toc="Complete Report Settings"
    >
      <Icon name="gear" class="jwx-button__icon" />
      Complete Report Settings
    </button>

    <WebViewVersionNumber styleClasses="jwx-page__version" />
  </div>

  <ModalCompleteReportSettings
    v-model:showSettingsModal="completeReportStore.showSettingsModal"
    v-if="completeReportStore.showSettingsModal"
  />
</template>